import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { i18n as I18nType } from 'i18next';
import axios from 'axios';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Collapse,
  Container,
  Form,
  Modal,
  Pagination,
  ProgressBar,
  Row,
  Spinner,
} from 'react-bootstrap';
import {
  careerService,
  CareerHome,
  CareerMissionCatalogItem,
  CareerMissionPlayerState,
  CareerMissionPublic,
  CareerMissionRequirementPublic,
  CareerRequirementProgress,
} from '../services/api';
import { useSearchParams } from 'react-router-dom';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { CAREER_SEARCH_QUERY_KEY } from '../constants/careerSearchParams';
import './QuizList.css';
import './CareerModeTab.css';

const CAREER_MISSIONS_PAGE_SIZE = 10;

/** "1 vez" / "10 vezes" (pt) ou "1 time" / "10 times" (en). */
function repeatWordForCount(n: number, i18n: I18nType): string {
  const lang = (i18n.language || 'en').toLowerCase();
  if (lang.startsWith('pt')) return n === 1 ? 'vez' : 'vezes';
  return n === 1 ? 'time' : 'times';
}

/** Primary quiz title vs `TitleEn` when the UI is in English (WITC bilingual quizzes). */
function quizTitleForLocale(
  titlePt: string | null | undefined,
  titleEn: string | null | undefined,
  lang: string | undefined
): string {
  const l = (lang || 'en').toLowerCase();
  const pt = titlePt?.trim() ?? '';
  const en = titleEn?.trim() ?? '';
  if (l.startsWith('en')) return en || pt;
  return pt || en;
}

/** Title + optional theme, e.g. "Quem é esse personagem? - Naruto Clássico". */
function quizTitlePlusTheme(
  titlePt: string | null | undefined,
  titleEn: string | null | undefined,
  themePt: string | null | undefined,
  themeEn: string | null | undefined,
  lang: string | undefined
): string {
  const title = quizTitleForLocale(titlePt, titleEn, lang).trim();
  const theme = quizTitleForLocale(themePt, themeEn, lang).trim();
  if (theme) return `${title} - ${theme}`;
  return title;
}

/** Missão só de desbloqueio Médio/Difícil no quiz-fonte (sem quiz extra como recompensa): UI/backend só no fácil. */
function missionRequirementsStrictEasyOnly(m: CareerMissionPublic): boolean {
  const unlocksBands =
    m.rewardUnlockPlayMedium === true || m.rewardUnlockPlayHard === true;
  const unlocksOtherQuiz = !!m.rewardUnlockQuizId;
  return unlocksBands && !unlocksOtherQuiz;
}

function formatRequirementLine(
  req: CareerMissionRequirementPublic,
  i18n: I18nType,
  sourceQuizCaption: string,
  strictEasyOnly: boolean
): React.ReactNode {
  const quizTitle =
    sourceQuizCaption.trim() ||
    i18n.t('career.sourceQuizFallback', { defaultValue: 'source quiz' });

  const type = req.requirementType;
  if (type === 'PERFECT_WINS_COUNT') {
    const n = req.targetCount ?? 0;
    const repeatWord = repeatWordForCount(n, i18n);
    return (
      <>
        {i18n.t('career.req.perfectWinsLead', {
          count: n,
          repeatWord,
          quizTitle,
          defaultValue:
            'Win {{count}} {{repeatWord}} with a perfect score in the quiz {{quizTitle}} ',
        })}
        <strong>
          {i18n.t(strictEasyOnly ? 'career.req.modeEasy' : 'career.req.modeAny', {
            defaultValue: strictEasyOnly ? 'in Easy mode' : 'on any difficulty mode',
          })}
        </strong>
        .
      </>
    );
  }
  if (type === 'QUIZ_PLAY_COUNT') {
    const n = req.targetCount ?? 0;
    const repeatWord = repeatWordForCount(n, i18n);
    return (
      <>
        {i18n.t('career.req.quizPlayLead', {
          count: n,
          repeatWord,
          quizTitle,
          defaultValue: 'Play the quiz {{quizTitle}} {{count}} {{repeatWord}} ',
        })}
        <strong>
          {i18n.t(strictEasyOnly ? 'career.req.modeEasy' : 'career.req.modeAny', {
            defaultValue: strictEasyOnly ? 'in Easy mode' : 'on any difficulty mode',
          })}
        </strong>
        .
      </>
    );
  }
  if (type === 'SPECIFIC_QUESTIONS_CORRECT_ONCE') {
    const n = req.specificQuestionCount ?? 0;
    if (n === 1) {
      return i18n.t('career.req.specificQuestionsOne', {
        defaultValue: 'Answer 1 specific question correctly at least once.',
      });
    }
    return i18n.t('career.req.specificQuestionsMany', {
      count: n,
      defaultValue: 'Answer {{count}} specific questions correctly at least once.',
    });
  }
  return type;
}

function requirementTarget(
  req: CareerMissionRequirementPublic,
  prog?: CareerRequirementProgress
): number {
  if (req.requirementType === 'SPECIFIC_QUESTIONS_CORRECT_ONCE') {
    return prog?.specificQuestionCount ?? req.specificQuestionCount ?? 0;
  }
  return req.targetCount ?? prog?.targetCount ?? 0;
}

function requirementCurrent(
  req: CareerMissionRequirementPublic,
  prog?: CareerRequirementProgress
): number {
  if (req.requirementType === 'SPECIFIC_QUESTIONS_CORRECT_ONCE') {
    return prog?.specificSatisfiedCount ?? 0;
  }
  return prog?.currentCount ?? 0;
}

function requirementIconClass(type: string): string {
  switch (type) {
    case 'PERFECT_WINS_COUNT':
      return 'bi-star-fill';
    case 'QUIZ_PLAY_COUNT':
      return 'bi-play-circle-fill';
    case 'SPECIFIC_QUESTIONS_CORRECT_ONCE':
      return 'bi-patch-check-fill';
    default:
      return 'bi-list-check';
  }
}

function isRequirementComplete(
  req: CareerMissionRequirementPublic,
  prog?: CareerRequirementProgress
): boolean {
  const t = requirementTarget(req, prog);
  const c = requirementCurrent(req, prog);
  if (t <= 0) return false;
  return c >= t;
}

/** Progresso agregado para barra do cartão (média dos requisitos). */
function getMissionProgressDisplay(
  m: CareerMissionPublic,
  player: CareerMissionPlayerState,
  status: 'none' | 'accepted' | 'paused' | 'completed'
): { barNow: number; fractionLabel: string } | null {
  if (status === 'none') return null;
  if (status === 'completed') return { barNow: 100, fractionLabel: '100%' };
  const reqs = [...m.requirements].sort((a, b) => a.sortOrder - b.sortOrder);
  if (reqs.length === 0) return null;
  let sumRatio = 0;
  const labels: string[] = [];
  reqs.forEach((r, idx) => {
    const prog = player.requirements[idx];
    const t = requirementTarget(r, prog);
    const c = requirementCurrent(r, prog);
    const ratio = t > 0 ? Math.min(1, c / t) : 0;
    sumRatio += ratio;
    labels.push(t > 0 ? `${Math.min(c, t)} / ${t}` : '—');
  });
  const avg = sumRatio / reqs.length;
  return {
    barNow: avg * 100,
    fractionLabel: reqs.length === 1 ? labels[0] ?? '0 / 0' : `${Math.round(avg * 100)}%`,
  };
}

/** Porcentagem da barra (0–100) para ordenação entre missões incompletas (mesma lógica da UI). */
function getMissionProgressBarNow(
  m: CareerMissionPublic,
  player: CareerMissionPlayerState,
  status: 'none' | 'accepted' | 'paused' | 'completed'
): number {
  if (status === 'none') return 0;
  if (status === 'completed') return 100;
  const disp = getMissionProgressDisplay(m, player, status);
  return disp?.barNow ?? 0;
}

function compareCareerCatalogItems(
  a: CareerMissionCatalogItem,
  b: CareerMissionCatalogItem,
  activeMissionId: string | null | undefined,
  lang: string | undefined
): number {
  const am = a.mission.id;
  const bm = b.mission.id;
  const as = a.player.playerStatus;
  const bs = b.player.playerStatus;

  const aActive = !!activeMissionId && am === activeMissionId;
  const bActive = !!activeMissionId && bm === activeMissionId;
  if (aActive !== bActive) return aActive ? -1 : 1;

  const aCompleted = as === 'completed';
  const bCompleted = bs === 'completed';
  if (aCompleted !== bCompleted) return aCompleted ? 1 : -1;

  const pa = getMissionProgressBarNow(a.mission, a.player, as);
  const pb = getMissionProgressBarNow(b.mission, b.player, bs);
  if (pb !== pa) return pb - pa;

  const ao = a.mission.sortOrder ?? 0;
  const bo = b.mission.sortOrder ?? 0;
  if (ao !== bo) return ao - bo;

  return missionDisplayTitle(a.mission, lang).localeCompare(
    missionDisplayTitle(b.mission, lang),
    undefined,
    { sensitivity: 'base' }
  );
}

function missionDisplayTitle(m: CareerMissionPublic, lang: string | undefined): string {
  const l = (lang || 'en').toLowerCase();
  if (l.startsWith('en') && m.titleEn?.trim()) return m.titleEn.trim();
  return m.title;
}

function missionDisplayDescription(m: CareerMissionPublic, lang: string | undefined): string | null {
  const l = (lang || 'en').toLowerCase();
  if (l.startsWith('en')) {
    const en = m.descriptionEn?.trim();
    if (en) return en;
    return m.description?.trim() ?? null;
  }
  return m.description?.trim() ?? null;
}

/** Texto agregado para busca (nome, temas, quiz recompensa e desbloqueio de dificuldades). */
function careerMissionSearchHaystack(m: CareerMissionPublic, lang: string | undefined): string {
  const parts: string[] = [];
  const push = (s: string | null | undefined) => {
    const t = s?.trim();
    if (t) parts.push(t);
  };

  push(m.title);
  push(m.titleEn);
  push(m.description);
  push(m.descriptionEn);
  push(missionDisplayTitle(m, lang));
  push(missionDisplayDescription(m, lang) ?? undefined);

  push(m.sourceQuizTitle);
  push(m.sourceQuizTitleEn);
  push(m.sourceQuizTheme);
  push(m.sourceQuizThemeEn);
  push(quizTitlePlusTheme(m.sourceQuizTitle, m.sourceQuizTitleEn, m.sourceQuizTheme, m.sourceQuizThemeEn, lang));
  push(quizTitlePlusTheme(m.sourceQuizTitle, m.sourceQuizTitleEn, m.sourceQuizTheme, m.sourceQuizThemeEn, 'pt'));
  push(quizTitlePlusTheme(m.sourceQuizTitle, m.sourceQuizTitleEn, m.sourceQuizTheme, m.sourceQuizThemeEn, 'en'));

  push(m.rewardUnlockQuizTitle);
  push(m.rewardUnlockQuizTitleEn);
  push(m.rewardUnlockQuizTheme);
  push(m.rewardUnlockQuizThemeEn);
  push(
    quizTitlePlusTheme(
      m.rewardUnlockQuizTitle,
      m.rewardUnlockQuizTitleEn,
      m.rewardUnlockQuizTheme,
      m.rewardUnlockQuizThemeEn,
      lang
    )
  );
  push(
    quizTitlePlusTheme(
      m.rewardUnlockQuizTitle,
      m.rewardUnlockQuizTitleEn,
      m.rewardUnlockQuizTheme,
      m.rewardUnlockQuizThemeEn,
      'pt'
    )
  );
  push(
    quizTitlePlusTheme(
      m.rewardUnlockQuizTitle,
      m.rewardUnlockQuizTitleEn,
      m.rewardUnlockQuizTheme,
      m.rewardUnlockQuizThemeEn,
      'en'
    )
  );

  push(m.rewardAchievementNamePt);
  push(m.rewardAchievementNameEn);

  if (m.rewardUnlockPlayMedium) {
    parts.push('medium', 'médio', 'medio');
  }
  if (m.rewardUnlockPlayHard) {
    parts.push('hard', 'difícil', 'dificil');
  }

  return parts.join(' ').toLowerCase();
}

function missionMatchesCareerSearch(m: CareerMissionPublic, lang: string | undefined, rawQuery: string): boolean {
  /** Alinha caption do Solo (`—`) com `quizTitlePlusTheme` da API (` - `). */
  const q = rawQuery
    .replace(/\u2014/g, '-')
    .replace(/\u2013/g, '-')
    .trim()
    .toLowerCase();
  if (!q) return true;
  const tokens = q.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;
  const hay = careerMissionSearchHaystack(m, lang)
    .replace(/\u2014/g, '-')
    .replace(/\u2013/g, '-');
  return tokens.every((tok) => hay.includes(tok));
}

export interface CareerModeTabProps {
  isUserLoggedIn?: boolean;
  /** Abre a aba Solo e destaca o quiz de origem na lista. */
  onGoToSoloTab?: (sourceQuizId: string) => void;
}

const CareerModeTab: React.FC<CareerModeTabProps> = ({
  isUserLoggedIn = false,
  onGoToSoloTab,
}) => {
  const { i18n, t } = useSafeTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [home, setHome] = useState<CareerHome | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [careerIntroOpen, setCareerIntroOpen] = useState(false);
  const [acceptingId, setAcceptingId] = useState<string | null>(null);
  const [pausingId, setPausingId] = useState<string | null>(null);
  const [resumingId, setResumingId] = useState<string | null>(null);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [soloModalMission, setSoloModalMission] = useState<CareerMissionPublic | null>(null);
  /** Keys `${missionId}-${requirementId}` — specific-question rows expanded in career cards. */
  const [openSpecificReq, setOpenSpecificReq] = useState<Record<string, boolean>>({});
  /** Mission cards: collapsed show title, badges, footer; expanded adds description, req, rewards */
  const [expandedCareerMissions, setExpandedCareerMissions] = useState<Record<string, boolean>>({});
  const [careerMissionPage, setCareerMissionPage] = useState(1);
  const [careerSearch, setCareerSearch] = useState('');
  const [careerMoreFiltersOpen, setCareerMoreFiltersOpen] = useState(false);
  const [hideCompletedCareer, setHideCompletedCareer] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAcceptError(null);
    try {
      if (isUserLoggedIn) {
        const data = await careerService.getCareerHome();
        setHome(data);
      } else {
        const list = await careerService.listActiveMissions();
        setHome({
          activeMissionId: null,
          missions: list.map((m) => ({
            mission: m,
            player: {
              missionId: m.id,
              playerStatus: 'none',
              requirements: [],
            },
          })),
        });
      }
    } catch {
      setError(
        i18n.t('career.loadError', {
          defaultValue: 'Could not load career missions. Try again later.',
        })
      );
      setHome(null);
    } finally {
      setLoading(false);
    }
  }, [i18n, isUserLoggedIn]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const q = searchParams.get(CAREER_SEARCH_QUERY_KEY);
    if (q != null && q !== '') {
      setCareerSearch(q);
    }
  }, [searchParams]);

  const clearCareerSearchFilters = () => {
    setCareerSearch('');
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(CAREER_SEARCH_QUERY_KEY);
        return next;
      },
      { replace: true }
    );
  };

  const achievementName = (m: CareerMissionPublic) => {
    const pt = i18n.language?.toLowerCase().startsWith('pt');
    if (pt) return m.rewardAchievementNamePt ?? m.rewardAchievementNameEn ?? '';
    return m.rewardAchievementNameEn ?? m.rewardAchievementNamePt ?? '';
  };

  const handleAccept = async (item: CareerMissionCatalogItem) => {
    if (!isUserLoggedIn) return;
    setAcceptingId(item.mission.id);
    setAcceptError(null);
    try {
      await careerService.acceptMission(item.mission.id);
      await load();
    } catch (e: unknown) {
      let msg = i18n.t('career.acceptError', {
        defaultValue: 'Could not start this mission. Try again.',
      });
      if (axios.isAxiosError(e) && e.response?.data && typeof e.response.data === 'object') {
        const m = (e.response.data as { message?: string }).message;
        if (m) msg = m;
      }
      setAcceptError(msg);
    } finally {
      setAcceptingId(null);
    }
  };

  const handlePause = async (missionId: string) => {
    if (!isUserLoggedIn) return;
    setPausingId(missionId);
    setAcceptError(null);
    try {
      await careerService.pauseMission(missionId);
      await load();
    } catch (e: unknown) {
      let msg = i18n.t('career.pauseError', {
        defaultValue: 'Could not pause this mission. Try again.',
      });
      if (axios.isAxiosError(e) && e.response?.data && typeof e.response.data === 'object') {
        const m = (e.response.data as { message?: string }).message;
        if (m) msg = m;
      }
      setAcceptError(msg);
    } finally {
      setPausingId(null);
    }
  };

  const handleResume = async (missionId: string) => {
    if (!isUserLoggedIn) return;
    setResumingId(missionId);
    setAcceptError(null);
    try {
      await careerService.resumeMission(missionId);
      await load();
    } catch (e: unknown) {
      let msg = i18n.t('career.resumeError', {
        defaultValue: 'Could not resume this mission. Try again.',
      });
      if (axios.isAxiosError(e) && e.response?.data && typeof e.response.data === 'object') {
        const m = (e.response.data as { message?: string }).message;
        if (m) msg = m;
      }
      setAcceptError(msg);
    } finally {
      setResumingId(null);
    }
  };

  const missions = home?.missions ?? [];

  /** Só busca textual (sem ocultar concluídas) — para mensagem de lista vazia. */
  const missionsMatchingSearch = useMemo(() => {
    return (home?.missions ?? []).filter((item) =>
      missionMatchesCareerSearch(item.mission, i18n.language, careerSearch)
    );
  }, [home, i18n.language, careerSearch]);

  const filteredMissions = useMemo(() => {
    let list = missionsMatchingSearch;
    if (hideCompletedCareer) {
      list = list.filter((item) => item.player.playerStatus !== 'completed');
    }
    return list;
  }, [missionsMatchingSearch, hideCompletedCareer]);

  const orderedMissions = useMemo(() => {
    const list = [...filteredMissions];
    list.sort((a, b) =>
      compareCareerCatalogItems(a, b, home?.activeMissionId ?? null, i18n.language)
    );
    return list;
  }, [filteredMissions, home?.activeMissionId, i18n.language]);

  useEffect(() => {
    setCareerMissionPage(1);
  }, [careerSearch, hideCompletedCareer]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(orderedMissions.length / CAREER_MISSIONS_PAGE_SIZE));
    setCareerMissionPage((p) => Math.min(p, totalPages));
  }, [orderedMissions.length]);

  const careerMissionsPageData = useMemo(() => {
    const totalCount = orderedMissions.length;
    const totalPages = Math.max(1, Math.ceil(totalCount / CAREER_MISSIONS_PAGE_SIZE));
    const page = Math.min(Math.max(1, careerMissionPage), totalPages);
    const start = (page - 1) * CAREER_MISSIONS_PAGE_SIZE;
    const pagedMissions = orderedMissions.slice(start, start + CAREER_MISSIONS_PAGE_SIZE);
    return {
      page,
      totalPages,
      totalCount,
      pagedMissions,
      hasNextPage: page < totalPages,
      hasPreviousPage: page > 1,
    };
  }, [orderedMissions, careerMissionPage]);

  const careerRangeStart =
    careerMissionsPageData.totalCount === 0
      ? 0
      : (careerMissionsPageData.page - 1) * CAREER_MISSIONS_PAGE_SIZE + 1;
  const careerRangeEnd = Math.min(
    careerMissionsPageData.page * CAREER_MISSIONS_PAGE_SIZE,
    careerMissionsPageData.totalCount
  );

  return (
    <Container fluid className="quiz-list-page career-mode-tab py-3">
      <Row className="mb-4">
        <Col>
          <div className="quiz-list-header">
            <h1>
              <span aria-hidden>🏁</span>{' '}
              {i18n.t('career.title', { defaultValue: 'Career mode' })}
            </h1>
            <p className="text-muted mb-0">
              {i18n.t('career.subtitle', {
                defaultValue: 'Take on the challenge, master the objectives, and earn exclusive rewards!',
              })}
            </p>
          </div>
        </Col>
      </Row>

      <Row>
        <Col>
          <Alert variant="light" className="quiz-scoring-disclaimer border mb-4 py-0">
            <button
              type="button"
              className="quiz-scoring-disclaimer-toggle w-100 text-start btn btn-link text-decoration-none text-body-secondary px-0 py-2"
              onClick={() => setCareerIntroOpen((o) => !o)}
              aria-expanded={careerIntroOpen}
              aria-controls="career-intro-panel"
              id="career-intro-trigger"
            >
              <span className="d-flex align-items-center gap-2 small w-100 min-w-0">
                <i className="bi bi-info-circle-fill text-primary flex-shrink-0" aria-hidden />
                <i
                  className={`bi ${careerIntroOpen ? 'bi-chevron-up' : 'bi-chevron-down'} flex-shrink-0`}
                  aria-hidden
                />
                <span className="text-truncate">
                  {i18n.t('career.learnMoreToggle', {
                    defaultValue: 'Learn more about Career mode',
                  })}
                </span>
              </span>
            </button>
            <Collapse in={careerIntroOpen}>
              <div
                id="career-intro-panel"
                className="border-top pb-3 pt-2 quiz-scoring-disclaimer-panel-top"
                role="region"
                aria-labelledby="career-intro-trigger"
              >
                <p className="mb-0 small text-body-secondary">
                  {i18n.t('career.learnMoreBody', {
                    defaultValue:
                      'Missions track your progress on the mission’s quiz — in quiz play or in Arena PvP. You can have one active mission at a time. Complete the requirements (for example: number of plays, perfect runs, or specific questions), then earn XP, achievements, and sometimes unlock new quizzes.',
                  })}
                </p>
              </div>
            </Collapse>
          </Alert>
        </Col>
      </Row>

      {loading && (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" variant="primary" />
        </div>
      )}

      {!loading && error && (
        <Alert variant="light" className="career-mode-tab__alert">
          {error}
        </Alert>
      )}

      {!loading && acceptError && (
        <Alert
          variant="warning"
          className="career-mode-tab__alert"
          dismissible
          onClose={() => setAcceptError(null)}
        >
          {acceptError}
        </Alert>
      )}

      {!loading && !error && missions.length === 0 && (
        <Alert variant="light" className="career-mode-tab__alert">
          {i18n.t('career.empty', { defaultValue: 'No missions available yet.' })}
        </Alert>
      )}

      {!loading && missions.length > 0 && (
        <>
          <Row className="mb-4">
            <Col>
              <Card>
                <Card.Body>
                  <div className="d-md-none">
                    <Form.Group className="mb-3">
                      <Form.Label>
                        {t('career.searchMissions', 'Search missions')}
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder={t(
                          'career.searchPlaceholder',
                          'Search by mission name, themes, or reward quiz…'
                        )}
                        value={careerSearch}
                        onChange={(e) => setCareerSearch(e.target.value)}
                        autoComplete="off"
                        id="career-mission-search-mobile"
                      />
                    </Form.Group>
                    <Form.Check
                      type="checkbox"
                      id="career-hide-completed-mobile"
                      className="career-mode-tab__hide-completed-inline mb-3"
                      label={t('career.hideCompleted', 'Hide completed missions')}
                      checked={hideCompletedCareer}
                      onChange={(e) => setHideCompletedCareer(e.target.checked)}
                    />
                    <Button
                      type="button"
                      variant="outline-secondary"
                      className="w-100 mb-2 d-flex align-items-center justify-content-center gap-2 quiz-list-more-filters-toggle"
                      onClick={() => setCareerMoreFiltersOpen((o) => !o)}
                      aria-expanded={careerMoreFiltersOpen}
                      aria-controls="career-mission-more-filters"
                      id="career-mission-more-filters-toggle"
                    >
                      <i className="bi bi-funnel" aria-hidden />
                      <span>{t('quiz.moreFilters', 'More filters')}</span>
                      <i
                        className={`bi ${careerMoreFiltersOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`}
                        aria-hidden
                      />
                    </Button>
                    <Collapse in={careerMoreFiltersOpen}>
                      <div id="career-mission-more-filters">
                        <Row className="g-3 mt-1">
                          <Col xs={12}>
                            <Button
                              variant="outline-secondary"
                              className="w-100"
                              type="button"
                              onClick={() => clearCareerSearchFilters()}
                            >
                              {t('quiz.clearFilters', 'Clear Filters')}
                            </Button>
                          </Col>
                        </Row>
                      </div>
                    </Collapse>
                  </div>

                  {/* outer d-none d-md-block separado: evita d-md-block no mesmo nó que o layout interno */}
                  <div className="d-none d-md-block career-mode-tab__filters-desktop-wrap">
                    <div className="career-mode-tab__filters-desktop-line-search">
                      <div className="career-mode-tab__filters-desktop-search">
                        <div className="career-mode-tab__filters-desktop-search-group">
                          <Form.Label
                            htmlFor="career-mission-search-desktop"
                            className="form-label career-mode-tab__filters-desktop-label"
                          >
                            {t('career.searchMissions', 'Search missions')}
                          </Form.Label>
                          <Form.Control
                            id="career-mission-search-desktop"
                            type="text"
                            className="career-mode-tab__filters-desktop-input"
                            placeholder={t(
                              'career.searchPlaceholder',
                              'Search by mission name, themes, or reward quiz…'
                            )}
                            value={careerSearch}
                            onChange={(e) => setCareerSearch(e.target.value)}
                            autoComplete="off"
                          />
                        </div>
                      </div>
                    </div>
                    {/* Mesma estrutura da aba PvP: Row mt-3 + Col auto + flex gap-2 (PvPLobby desktop) */}
                    <Row className="justify-content-center mt-3">
                      <Col
                        xs="auto"
                        className="d-flex justify-content-center align-items-center flex-wrap gap-2 career-mode-tab__filters-desktop-actions-inner"
                      >
                        <Form.Check
                          type="checkbox"
                          id="career-hide-completed-desktop"
                          className="career-mode-tab__hide-completed-inline mb-0"
                          label={t('career.hideCompleted', 'Hide completed missions')}
                          checked={hideCompletedCareer}
                          onChange={(e) => setHideCompletedCareer(e.target.checked)}
                        />
                        <Button
                          type="button"
                          variant="outline-secondary"
                          className="pvp-clear-filters-btn w-auto text-nowrap"
                          onClick={() => clearCareerSearchFilters()}
                        >
                          {t('quiz.clearFilters', 'Clear Filters')}
                        </Button>
                      </Col>
                    </Row>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {orderedMissions.length === 0 ? (
            <Alert variant="light" className="career-mode-tab__alert">
              {missionsMatchingSearch.length === 0
                ? t('career.searchNoResults', 'No missions match your search.')
                : hideCompletedCareer
                  ? t(
                      'career.emptyWhenHideCompleted',
                      'All matching missions are completed. Uncheck hide completed to see them.'
                    )
                  : t('career.searchNoResults', 'No missions match your search.')}
            </Alert>
          ) : (
            <>
            <Row className="mb-3">
              <Col>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <p className="text-muted mb-0 flex-grow-1 min-w-0">
                    {i18n.t('career.paginationSummary', {
                      start: careerRangeStart,
                      end: careerRangeEnd,
                      total: careerMissionsPageData.totalCount,
                      defaultValue: 'Showing {{start}} to {{end}} of {{total}} missions',
                    })}
                  </p>
                  <p className="text-muted mb-0 text-nowrap flex-shrink-0">
                    {i18n.t('career.paginationPage', {
                      page: careerMissionsPageData.page,
                      totalPages: careerMissionsPageData.totalPages,
                      defaultValue: 'Page {{page}} of {{totalPages}}',
                    })}
                  </p>
                </div>
              </Col>
            </Row>
            <Row className="g-4 career-mission-list">
          {careerMissionsPageData.pagedMissions.map((item) => {
            const m = item.mission;
            const player = item.player;
            const status = player.playerStatus;
            const activeElsewhere =
              !!home?.activeMissionId &&
              home.activeMissionId !== m.id &&
              status === 'none';

            const sortedReqs = [...m.requirements].sort((a, b) => a.sortOrder - b.sortOrder);
            const sourceQuizCaption = quizTitlePlusTheme(
              m.sourceQuizTitle,
              m.sourceQuizTitleEn,
              m.sourceQuizTheme,
              m.sourceQuizThemeEn,
              i18n.language
            );
            const headerProgress = getMissionProgressDisplay(m, player, status);
            const statusLabel =
              status === 'completed'
                ? i18n.t('career.statusCompleted', { defaultValue: 'Completed' })
                : status === 'accepted'
                  ? i18n.t('career.statusInProgress', { defaultValue: 'In progress' })
                  : status === 'paused'
                    ? i18n.t('career.statusPaused', { defaultValue: 'Paused' })
                    : i18n.t('career.statusAvailable', { defaultValue: 'Available' });

            const missionDesc = missionDisplayDescription(m, i18n.language);
            /** Todos os cards começam expandidos; o jogador pode recolher por missão. */
            const isExpanded = expandedCareerMissions[m.id] !== false;

            return (
              <Col xs={12} key={m.id}>
                <article
                  className={`career-mission-card career-mission-card--light quiz-card${isExpanded ? ' career-mission-card--expanded' : ' career-mission-card--collapsed'}`}
                >
                  <div className="career-mission-card__top">
                    <div className="career-mission-card__head-main">
                      <div className="d-flex flex-wrap align-items-start gap-2 w-100">
                        <h3 className="career-mission-card__title quiz-title mb-0 flex-grow-1 min-w-0">
                          {missionDisplayTitle(m, i18n.language)}
                        </h3>
                        <div className="d-flex flex-wrap align-items-center gap-2 ms-auto flex-shrink-0 career-mission-card__head-actions">
                          {m.sourceQuizTitle || m.sourceQuizTitleEn ? (
                            <Badge
                              bg="info"
                              text="white"
                              className="career-mission-card__badge fw-bold rounded-pill"
                            >
                              {quizTitlePlusTheme(
                                m.sourceQuizTitle,
                                m.sourceQuizTitleEn,
                                m.sourceQuizTheme,
                                m.sourceQuizThemeEn,
                                i18n.language
                              )}
                            </Badge>
                          ) : null}
                          {status === 'completed' ? (
                            <Badge
                              bg="success"
                              text="white"
                              className="career-mission-card__badge fw-bold rounded-pill"
                            >
                              {i18n.t('career.statusCompleted', { defaultValue: 'Completed' })}
                            </Badge>
                          ) : null}
                          {status === 'paused' ? (
                            <Badge
                              bg="secondary"
                              text="white"
                              className="career-mission-card__badge fw-bold rounded-pill"
                            >
                              {i18n.t('career.statusPaused', { defaultValue: 'Paused' })}
                            </Badge>
                          ) : null}
                          <button
                            type="button"
                            className="btn btn-link career-mission-card__expand-toggle text-body-secondary text-decoration-none p-1 border-0 lh-1"
                            aria-expanded={isExpanded}
                            aria-controls={`career-mission-expand-${m.id}`}
                            id={`career-mission-expand-btn-${m.id}`}
                            onClick={() =>
                              setExpandedCareerMissions((prev) => {
                                const wasExpanded = prev[m.id] !== false;
                                return { ...prev, [m.id]: !wasExpanded };
                              })
                            }
                          >
                            <span className="visually-hidden">
                              {isExpanded
                                ? i18n.t('career.collapseMissionCard', {
                                    defaultValue: 'Collapse mission details',
                                  })
                                : i18n.t('career.expandMissionCard', {
                                    defaultValue: 'Expand mission details',
                                  })}
                            </span>
                            <i
                              className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`}
                              aria-hidden
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                      <Collapse in={isExpanded}>
                        <div id={`career-mission-expand-${m.id}`} className="career-mission-card__expand">
                          {missionDesc || (headerProgress && isUserLoggedIn) ? (
                            <div className="career-mission-card__expand-meta">
                              {missionDesc ? (
                                <p className="career-mission-card__description career-mission-card__description--expand mb-0">
                                  {missionDesc}
                                </p>
                              ) : null}
                              {headerProgress && isUserLoggedIn ? (
                                <div className="career-mission-card__header-progress">
                                  <ProgressBar
                                    now={headerProgress.barNow}
                                    variant={
                                      status === 'completed'
                                        ? 'success'
                                        : status === 'paused'
                                          ? 'secondary'
                                          : 'info'
                                    }
                                    className="career-mission-card__header-bar"
                                    visuallyHidden
                                    aria-label={i18n.t('career.progressLabel', {
                                      defaultValue: 'Progress',
                                    })}
                                  />
                                  <span className="career-mission-card__header-fraction">
                                    {headerProgress.fractionLabel}
                                  </span>
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                          <div className="career-mission-card__grid career-mission-card__grid--expand">
                    <section
                      className="career-mission-card__panel career-mission-card__panel--req"
                      aria-label={i18n.t('career.requirements', { defaultValue: 'Requirements' })}
                    >
                      <h4 className="career-mission-card__panel-label">
                        {i18n.t('career.requirements', { defaultValue: 'Requirements' })}
                      </h4>
                      <ul className="career-mission-card__req-items list-unstyled mb-0">
                        {sortedReqs.map((r, idx) => {
                          const prog = status === 'none' ? undefined : player.requirements[idx];
                          const target = requirementTarget(r, prog);
                          const current = requirementCurrent(r, prog);
                          const showBar =
                            isUserLoggedIn &&
                            (status === 'accepted' || status === 'paused' || status === 'completed') &&
                            target > 0;
                          const done = prog ? isRequirementComplete(r, prog) : false;

                          const specSlots = prog?.specificQuestions;
                          const specGroups = prog?.specificQuestionGroups;
                          const catalogGroups = r.questionGroupsCatalog;
                          const catalogFlat = r.specificQuestionAnswerPreviews;
                          const hasProgressGrouped =
                            Array.isArray(specGroups) &&
                            specGroups.length > 0 &&
                            specGroups.some((g) => Array.isArray(g.slots) && g.slots.length > 0);
                          const hasProgressFlat =
                            Array.isArray(specSlots) && specSlots.length > 0;
                          const hasCatalogGrouped =
                            Array.isArray(catalogGroups) &&
                            catalogGroups.length > 0 &&
                            catalogGroups.some(
                              (g) => Array.isArray(g.answerLabels) && g.answerLabels.length > 0
                            );
                          const hasCatalogFlat =
                            Array.isArray(catalogFlat) && catalogFlat.length > 0;
                          const canExpandSpecific =
                            r.requirementType === 'SPECIFIC_QUESTIONS_CORRECT_ONCE' &&
                            (hasProgressGrouped ||
                              hasProgressFlat ||
                              hasCatalogGrouped ||
                              hasCatalogFlat);
                          const expandKey =
                            prog != null ? `${m.id}-${prog.requirementId}` : `${m.id}-req-${idx}`;
                          const isSpecOpen = !!openSpecificReq[expandKey];

                          const reqLine = (
                            <div className="career-mission-card__req-line">
                              <i
                                className={`bi ${requirementIconClass(r.requirementType)} career-mission-card__req-ico`}
                                aria-hidden
                              />
                              <span className="career-mission-card__req-text">
                                {formatRequirementLine(
                                  r,
                                  i18n,
                                  sourceQuizCaption,
                                  missionRequirementsStrictEasyOnly(m)
                                )}
                              </span>
                              {done ? (
                                <i
                                  className="bi bi-check-circle-fill career-mission-card__req-done"
                                  aria-label={i18n.t('career.reqDone', { defaultValue: 'Done' })}
                                />
                              ) : null}
                              {canExpandSpecific ? (
                                <i
                                  className={`bi ${isSpecOpen ? 'bi-chevron-up' : 'bi-chevron-down'} career-mission-card__specific-chevron`}
                                  aria-hidden
                                />
                              ) : null}
                            </div>
                          );

                          const reqBar = showBar ? (
                            <div className="career-mission-card__req-bar-wrap">
                                  <ProgressBar
                                    now={Math.min(100, target > 0 ? (current / target) * 100 : 0)}
                                    variant={
                                      current >= target
                                        ? 'success'
                                        : status === 'paused'
                                          ? 'secondary'
                                          : 'info'
                                    }
                                    className="career-mission-card__req-bar"
                                visuallyHidden
                                aria-label={i18n.t('career.progressAria', {
                                  current,
                                  target,
                                  defaultValue: 'Progress {{current}} of {{target}}',
                                })}
                              />
                              <span className="career-mission-card__req-meta">
                                {Math.min(current, target)} / {target}
                              </span>
                            </div>
                          ) : null;

                          return (
                            <li key={`${m.id}-req-${idx}`} className="career-mission-card__req-row">
                              {canExpandSpecific ? (
                                <>
                                  <button
                                    type="button"
                                    className="career-mission-card__specific-toggle"
                                    aria-expanded={isSpecOpen}
                                    aria-controls={`career-spec-${expandKey}`}
                                    aria-label={i18n.t('career.toggleSpecificQuestions', {
                                      defaultValue: 'Show or hide progress for each required question',
                                    })}
                                    onClick={() =>
                                      setOpenSpecificReq((prev) => ({
                                        ...prev,
                                        [expandKey]: !prev[expandKey],
                                      }))
                                    }
                                  >
                                    {reqLine}
                                    {reqBar}
                                  </button>
                                  <Collapse in={isSpecOpen}>
                                    <div
                                      id={`career-spec-${expandKey}`}
                                      className="career-mission-card__specific-badges-wrap"
                                    >
                                      {hasProgressGrouped ? (
                                        <div className="career-mission-card__specific-groups-row">
                                          {specGroups!.map((group, gi) => (
                                            <div
                                              key={`${expandKey}-grp-${gi}`}
                                              className="career-mission-card__specific-group-box"
                                            >
                                              <div className="career-mission-card__specific-group-title">
                                                {group.label}
                                              </div>
                                              <div className="career-mission-card__specific-badges">
                                                {group.slots.map((slot) => (
                                                  <Badge
                                                    key={slot.questionId}
                                                    pill
                                                    bg={slot.satisfied ? 'success' : 'dark'}
                                                    text="white"
                                                    className="career-mission-card__specific-badge"
                                                  >
                                                    {slot.satisfied && slot.revealedAnswer
                                                      ? slot.revealedAnswer
                                                      : slot.satisfied
                                                        ? i18n.t('career.specificAnswerOk', {
                                                            defaultValue: '✓',
                                                          })
                                                        : '???'}
                                                  </Badge>
                                                ))}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      ) : hasProgressFlat ? (
                                        <div className="career-mission-card__specific-badges">
                                          {specSlots!.map((slot) => (
                                            <Badge
                                              key={slot.questionId}
                                              pill
                                              bg={slot.satisfied ? 'success' : 'dark'}
                                              text="white"
                                              className="career-mission-card__specific-badge"
                                            >
                                              {slot.satisfied && slot.revealedAnswer
                                                ? slot.revealedAnswer
                                                : slot.satisfied
                                                  ? i18n.t('career.specificAnswerOk', {
                                                      defaultValue: '✓',
                                                    })
                                                  : '???'}
                                            </Badge>
                                          ))}
                                        </div>
                                      ) : hasCatalogGrouped ? (
                                        <div className="career-mission-card__specific-groups-row">
                                          {catalogGroups!.map((group, gi) => (
                                            <div
                                              key={`${expandKey}-cat-${gi}`}
                                              className="career-mission-card__specific-group-box"
                                            >
                                              <div className="career-mission-card__specific-group-title">
                                                {group.label}
                                              </div>
                                              <div className="career-mission-card__specific-badges">
                                                {group.answerLabels.map((_, li) => (
                                                  <Badge
                                                    key={`${expandKey}-cl-${gi}-${li}`}
                                                    pill
                                                    bg="dark"
                                                    text="white"
                                                    className="career-mission-card__specific-badge"
                                                  >
                                                    ???
                                                  </Badge>
                                                ))}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      ) : hasCatalogFlat ? (
                                        <div className="career-mission-card__specific-badges">
                                          {catalogFlat!.map((_, li) => (
                                            <Badge
                                              key={`${expandKey}-cf-${li}`}
                                              pill
                                              bg="dark"
                                              text="white"
                                              className="career-mission-card__specific-badge"
                                            >
                                              ???
                                            </Badge>
                                          ))}
                                        </div>
                                      ) : null}
                                    </div>
                                  </Collapse>
                                </>
                              ) : (
                                <>
                                  {reqLine}
                                  {reqBar}
                                </>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </section>

                    <section
                      className="career-mission-card__panel career-mission-card__panel--reward"
                      aria-label={i18n.t('career.rewards', { defaultValue: 'Rewards' })}
                    >
                      <h4 className="career-mission-card__panel-label">
                        {i18n.t('career.rewards', { defaultValue: 'Rewards' })}
                      </h4>
                      <div className="career-mission-card__rewards">
                        {m.rewardXp > 0 ? (
                          <div className="career-mission-card__reward-tile career-mission-card__reward-tile--xp">
                            <span className="career-mission-card__reward-tile-label">XP</span>
                            <span className="career-mission-card__reward-tile-value">{m.rewardXp}</span>
                          </div>
                        ) : null}
                        {m.rewardUnlockQuizTitle || m.rewardUnlockQuizTitleEn ? (
                          <div className="career-mission-card__reward-tile career-mission-card__reward-tile--unlock">
                            <i className="bi bi-unlock-fill career-mission-card__reward-ico" aria-hidden />
                            <p className="career-mission-card__reward-tile-text mb-0">
                              {i18n.t('career.rewardUnlockShort', {
                                title: quizTitlePlusTheme(
                                  m.rewardUnlockQuizTitle,
                                  m.rewardUnlockQuizTitleEn,
                                  m.rewardUnlockQuizTheme,
                                  m.rewardUnlockQuizThemeEn,
                                  i18n.language
                                ),
                                defaultValue: 'Unlocks quiz: {{title}}',
                              })}
                            </p>
                          </div>
                        ) : null}
                        {m.rewardAchievementId && achievementName(m) ? (
                          <div className="career-mission-card__reward-tile career-mission-card__reward-tile--ach">
                            <i className="bi bi-trophy-fill career-mission-card__reward-ico" aria-hidden />
                            <div>
                              <span className="career-mission-card__reward-ach-label">
                                {i18n.t('career.achievementLabel', {
                                  defaultValue: 'Achievement',
                                })}
                              </span>
                              <p className="career-mission-card__reward-tile-text mb-0 fw-semibold">
                                {achievementName(m)}
                              </p>
                            </div>
                          </div>
                        ) : null}
                        {m.rewardUnlockPlayMedium || m.rewardUnlockPlayHard ? (
                          <div className="career-mission-card__reward-tile career-mission-card__reward-tile--unlock career-mission-card__reward-tile--play-modes">
                            <i className="bi bi-unlock-fill career-mission-card__reward-ico" aria-hidden />
                            <div className="career-mission-card__reward-tile-text mb-0">
                              {(m.rewardUnlockPlayMedium && m.rewardUnlockPlayHard) ||
                              (m.rewardUnlockPlayHard && !m.rewardUnlockPlayMedium) ? (
                                <span className="d-block">
                                  {i18n.t('career.rewardUnlockMediumHardQuizLine', {
                                    quizTitle: sourceQuizCaption,
                                    defaultValue:
                                      'Unlocks Medium and Hard modes for the quiz: {{quizTitle}}',
                                  })}
                                </span>
                              ) : (
                                <span className="d-block">
                                  {i18n.t('career.rewardUnlockMediumSolo', {
                                    quizTitle: sourceQuizCaption,
                                    defaultValue:
                                      'Unlocks Medium (solo) mode for the quiz {{quizTitle}}.',
                                  })}
                                </span>
                              )}
                            </div>
                          </div>
                        ) : null}
                        {m.rewardXp <= 0 &&
                        !m.rewardUnlockQuizTitle &&
                        !m.rewardUnlockQuizTitleEn &&
                        !m.rewardAchievementId &&
                        !m.rewardUnlockPlayMedium &&
                        !m.rewardUnlockPlayHard ? (
                          <p className="career-mission-card__reward-none small mb-0">
                            {i18n.t('career.rewardNone', {
                              defaultValue: 'No extra rewards listed.',
                            })}
                          </p>
                        ) : null}
                      </div>
                    </section>
                  </div>
                        </div>
                      </Collapse>

                  <footer className="career-mission-card__footer">
                    <div className="career-mission-card__footer-status small text-muted">
                      <span className="career-mission-card__footer-status-key">
                        {i18n.t('career.footerStatusKey', { defaultValue: 'Status' })}
                      </span>{' '}
                      <span className="career-mission-card__footer-status-val">{statusLabel}</span>
                    </div>
                    <div className="career-mission-card__footer-actions">
                      {isUserLoggedIn && status === 'none' && (
                        <Button
                          type="button"
                          variant="primary"
                          className="quiz-card-start-btn"
                          disabled={
                            !!acceptingId ||
                            !!pausingId ||
                            !!resumingId ||
                            activeElsewhere
                          }
                          onClick={() => void handleAccept(item)}
                        >
                          {acceptingId === m.id ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              {i18n.t('career.startingMission', {
                                defaultValue: 'Starting…',
                              })}
                            </>
                          ) : (
                            <>
                              <i className="bi bi-flag-fill me-2" aria-hidden />
                              {i18n.t('career.acceptMission', {
                                defaultValue: 'Accept mission',
                              })}
                            </>
                          )}
                        </Button>
                      )}
                      {isUserLoggedIn && activeElsewhere && status === 'none' && (
                        <span className="career-mission-card__hint small">
                          {i18n.t('career.blockedOtherMission', {
                            defaultValue: 'Finish your current mission before starting another.',
                          })}
                        </span>
                      )}
                      {!isUserLoggedIn && (
                        <span className="career-mission-card__hint small">
                          {i18n.t('career.loginToStart', {
                            defaultValue: 'Log in to accept missions and track progress.',
                          })}
                        </span>
                      )}
                      {isUserLoggedIn && status === 'accepted' && (
                        <Button
                          type="button"
                          variant="outline-secondary"
                          className="quiz-card-start-btn me-2 mb-2"
                          disabled={!!acceptingId || !!pausingId || !!resumingId}
                          onClick={() => void handlePause(m.id)}
                        >
                          {pausingId === m.id ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              {i18n.t('career.pausingMission', { defaultValue: 'Pausing…' })}
                            </>
                          ) : (
                            <>
                              <i className="bi bi-pause-fill me-2" aria-hidden />
                              {i18n.t('career.pauseMission', { defaultValue: 'Pause mission' })}
                            </>
                          )}
                        </Button>
                      )}
                      {isUserLoggedIn && status === 'accepted' && onGoToSoloTab && (
                        <Button
                          type="button"
                          variant="outline-primary"
                          className="quiz-card-start-btn mb-2"
                          disabled={!!acceptingId || !!pausingId || !!resumingId}
                          onClick={() => setSoloModalMission(m)}
                        >
                          <i className="bi bi-play-fill me-2" aria-hidden />
                          {i18n.t('career.openSoloModal', {
                            defaultValue: 'Play quiz',
                          })}
                        </Button>
                      )}
                      {isUserLoggedIn && status === 'paused' && (
                        <>
                          <Button
                            type="button"
                            variant="primary"
                            className="quiz-card-start-btn mb-2"
                            disabled={!!acceptingId || !!pausingId || !!resumingId}
                            onClick={() => void handleResume(m.id)}
                          >
                            {resumingId === m.id ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                {i18n.t('career.resumingMission', { defaultValue: 'Resuming…' })}
                              </>
                            ) : (
                              <>
                                <i className="bi bi-play-fill me-2" aria-hidden />
                                {i18n.t('career.resumeMission', { defaultValue: 'Resume mission' })}
                              </>
                            )}
                          </Button>
                          <p className="career-mission-card__hint small text-muted mb-0 w-100">
                            {i18n.t('career.pausedMissionHint', {
                              defaultValue:
                                'Quiz and PvP runs do not advance this mission while it is paused. Your progress is saved.',
                            })}
                          </p>
                        </>
                      )}
                      {isUserLoggedIn && status === 'completed' && (
                        <Badge
                          bg="success"
                          text="white"
                          className="career-mission-card__badge fw-bold rounded-pill align-self-center"
                        >
                          {i18n.t('career.missionCompleteBadge', {
                            defaultValue: 'Mission complete',
                          })}
                        </Badge>
                      )}
                    </div>
                  </footer>
                </article>
              </Col>
            );
          })}
            </Row>

          {careerMissionsPageData.totalPages > 1 && (
            <Row className="mt-4">
              <Col className="d-flex justify-content-center">
                <Pagination>
                  <Pagination.First
                    disabled={!careerMissionsPageData.hasPreviousPage}
                    onClick={() => setCareerMissionPage(1)}
                  />
                  <Pagination.Prev
                    disabled={!careerMissionsPageData.hasPreviousPage}
                    onClick={() => setCareerMissionPage((p) => Math.max(1, p - 1))}
                  />
                  {Array.from(
                    { length: Math.min(5, careerMissionsPageData.totalPages) },
                    (_, i) => {
                      const pageNum =
                        Math.max(
                          1,
                          Math.min(
                            careerMissionsPageData.totalPages - 4,
                            careerMissionsPageData.page - 2
                          )
                        ) + i;
                      return (
                        <Pagination.Item
                          key={pageNum}
                          active={pageNum === careerMissionsPageData.page}
                          onClick={() => setCareerMissionPage(pageNum)}
                        >
                          {pageNum}
                        </Pagination.Item>
                      );
                    }
                  )}
                  <Pagination.Next
                    disabled={!careerMissionsPageData.hasNextPage}
                    onClick={() =>
                      setCareerMissionPage((p) =>
                        Math.min(careerMissionsPageData.totalPages, p + 1)
                      )
                    }
                  />
                  <Pagination.Last
                    disabled={!careerMissionsPageData.hasNextPage}
                    onClick={() => setCareerMissionPage(careerMissionsPageData.totalPages)}
                  />
                </Pagination>
              </Col>
            </Row>
          )}
            </>
          )}
        </>
      )}

      <Modal show={!!soloModalMission} onHide={() => setSoloModalMission(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {i18n.t('career.soloModalTitle', {
              defaultValue: 'Play quiz',
            })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            {i18n.t('career.soloModalBody', {
              quiz: soloModalMission
                ? quizTitlePlusTheme(
                    soloModalMission.sourceQuizTitle,
                    soloModalMission.sourceQuizTitleEn,
                    soloModalMission.sourceQuizTheme,
                    soloModalMission.sourceQuizThemeEn,
                    i18n.language
                  )
                : '',
              defaultValue:
                'Mission progress counts for valid runs in quiz play or PvP on this quiz: {{quiz}}.',
            })}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSoloModalMission(null)}>
            {i18n.t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              if (soloModalMission && onGoToSoloTab) {
                onGoToSoloTab(soloModalMission.sourceQuizId);
              }
              setSoloModalMission(null);
            }}
          >
            {i18n.t('career.goToSoloTab', { defaultValue: 'Play' })}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CareerModeTab;
